import React, { useState } from 'react';
import { 
    Modal, 
    Form, 
    Select, 
    DatePicker, 
    TimePicker, 
    Button, 
    Alert, 
    Space, 
    Typography,
    Divider
} from 'antd';
import { 
    ClockCircleOutlined, 
    CalendarOutlined, 
    InfoCircleOutlined,
    CoffeeOutlined
} from '@ant-design/icons';
import { oneKlicks } from '../../../SDK/oneclick';
import { toast } from 'react-toastify';
import moment from 'moment';

const { Text, Title } = Typography;
const { Option } = Select;

const BreakTimeModal = ({ isOpen, onClose, appointment, refresh }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [breakType, setBreakType] = useState(null);

    // Helper to convert '0900' to '09:00'
    const formatStringTime = (time) => {
        if (!time || time === 'closed') return 'Closed';
        let str = time.toString().padStart(4, '0');
        const hours = str.slice(0, 2);
        const minutes = str.slice(2, 4);
        return `${hours}:${minutes}`;
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const data = {
                break_type: values.breakType,
                from_time: values.breakType === 'some_time' ? values.timeRange[0].format('HH:mm') : '00:00',
                to_time: values.breakType === 'some_time' ? values.timeRange[1].format('HH:mm') : '23:59',
                select_date: values.select_date.format('YYYY-MM-DD'),
                hospital_id: appointment.hospital_id,
                doctor_id: appointment.doctor_id,
                appointment_setting_id: appointment.id,
                status: 'active'
            };

            const response = await oneKlicks.create('', 'doctor_break_time', data, {});
            if (response.success) {
                toast.success('Break time scheduled successfully');
                form.resetFields();
                onClose();
                refresh();
            } else {
                toast.error('Failed to save break time');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <CoffeeOutlined style={{ color: '#ed8936' }} />
                    <span>Schedule Break Time</span>
                </Space>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={500}
            centered
            className="premium-modal"
        >
            <div className="mb-4">
                <Alert
                    message="Availability Notice"
                    description={`Managing breaks for ${appointment?.hospital_name?.[0]?.display_name || 'this location'}. Appointments during break times will be blocked.`}
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ borderRadius: '10px' }}
                />
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ breakType: 'full_day' }}
                onValuesChange={(changed) => {
                    if (changed.breakType) setBreakType(changed.breakType);
                }}
            >
                <Form.Item
                    name="select_date"
                    label={<Text strong>Select Date</Text>}
                    rules={[{ required: true, message: 'Please select a date' }]}
                >
                    <DatePicker 
                        style={{ width: '100%' }} 
                        prefix={<CalendarOutlined />} 
                        placeholder="Choose break date"
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    name="breakType"
                    label={<Text strong>Break Duration</Text>}
                    rules={[{ required: true }]}
                >
                    <Select size="large">
                        <Option value="full_day">Full Day (Block all slots)</Option>
                        <Option value="some_time">Partial Day (Specific hours)</Option>
                    </Select>
                </Form.Item>

                {breakType === 'some_time' && (
                    <div className="animate__animated animate__fadeIn">
                        <div className="p-3 mb-3" style={{ background: '#fffaf0', border: '1px solid #feebc8', borderRadius: '8px' }}>
                            <Text size="small" type="secondary">
                                Clinic Hours: <strong>{formatStringTime(appointment.start_time)}</strong> - <strong>{formatStringTime(appointment.end_time)}</strong>
                            </Text>
                        </div>
                        
                        <Form.Item
                            name="timeRange"
                            label={<Text strong>Specify Time Range</Text>}
                            rules={[{ required: true, message: 'Please select time range' }]}
                        >
                            <TimePicker.RangePicker 
                                style={{ width: '100%' }} 
                                format="HH:mm" 
                                size="large"
                                minuteStep={15}
                            />
                        </Form.Item>
                    </div>
                )}

                <Divider />

                <div className="d-flex justify-content-end gap-2">
                    <Button onClick={onClose} size="large" style={{ borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading} 
                        size="large"
                        style={{ borderRadius: '8px', background: '#ed8936', borderColor: '#ed8936' }}
                    >
                        Schedule Break
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default BreakTimeModal;

